import { useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import DatePicker from '@components/DatePicker/DatePicker';
import {
  FormValues,
  TaskFormStackScreenProps,
} from '@components/Tasks/TaskFormStack';
import { TaskStatus } from '@graphql/generated';
import { beginningOfToday, localDateFromUTCDate } from '@utils/formatters/date';

type DateSelectPickerProps = {
  onClose?: () => void;
  formField?: 'startDate' | 'dueDate';
  isBottomSheet?: boolean;
  submitWhenSelected?: boolean;
  /** Forwarded to `DatePicker` — see its own doc comment. */
  shouldHugContent?: boolean;
};

export const DateSelectPicker: React.FC<DateSelectPickerProps> = ({
  onClose,
  formField,
  isBottomSheet,
  submitWhenSelected = false,
  shouldHugContent,
}) => {
  const route = useRoute<TaskFormStackScreenProps<'date-select'>['route']>();
  const { params } = route;
  const { field: paramsField } = params || { paramsField: undefined };

  const field = paramsField || formField;

  if (!field) return null;

  const { t } = useTranslation('format');

  const {
    setFieldValue,
    setTouched,
    touched,
    getFieldProps,
    values,
    submitForm,
    isSubmitting,
  } = useFormikContext<FormValues>();
  const { startDate, dueDate } = values;
  const { name: fieldName, value } = getFieldProps(field);
  return (
    <DatePicker
      isSubmitting={isSubmitting}
      onClose={onClose}
      isBottomSheet={isBottomSheet}
      shouldHugContent={shouldHugContent}
      isLoading={submitWhenSelected && isSubmitting}
      minDate={
        fieldName === 'dueDate' && startDate
          ? localDateFromUTCDate(startDate)
          : undefined
      }
      maxDate={
        fieldName === 'startDate' && dueDate
          ? localDateFromUTCDate(dueDate)
          : undefined
      }
      title={
        fieldName === 'startDate'
          ? t('models:tasks.create.dates.startDate')
          : t('models:tasks.create.dates.dueDate')
      }
      selectedDate={value != null ? localDateFromUTCDate(value) : null}
      onSelectDate={async (date: Date | undefined) => {
        setTouched({ ...touched, [fieldName]: true });
        // Formik state commits happen on the microtask the returned promise
        // resolves on — submitForm() must wait for that, or it reads the
        // pre-update values (stale dueDate/status) instead of what was just
        // selected.
        await setFieldValue(fieldName, date ?? undefined);

        const today = beginningOfToday();

        if (date) {
          const isToday = dayjs().isSame(date, 'day');
          const isPast = date < today;
          const isFuture = date > today;

          if (fieldName === 'startDate') {
            if (isFuture) {
              await setFieldValue('status', TaskStatus.NotStarted);
            } else if (isPast || isToday) {
              await setFieldValue('status', TaskStatus.Ongoing);
            }
            if (dueDate && dueDate < today) {
              await setFieldValue('status', TaskStatus.Overdue);
            }
          }

          if (fieldName === 'dueDate') {
            if (isPast) {
              await setFieldValue('status', TaskStatus.Overdue);
            } else if (isFuture || isToday) {
              if (
                startDate &&
                (startDate < today || dayjs().isSame(startDate, 'day'))
              ) {
                await setFieldValue('status', TaskStatus.Ongoing);
              } else {
                await setFieldValue('status', TaskStatus.NotStarted);
              }
            }
          }
        }
        if (submitWhenSelected) submitForm();
        else onClose?.();
      }}
    />
  );
};
